import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React from "react";
import CronExpressionBuilder, { ADMIN_CRON_PRESETS } from "../../components/CronExpressionBuilder";
import { getAllPuzzleTypes } from "../../pzl_common/PuzzleTypeConstants";

const LANGUAGES = [
    { value: "all", label: "All" },
    { value: "english", label: "English" },
    { value: "french", label: "French" }
];

export default function PubTabScheduler({
    cron,
    onCronChange,
    language,
    onLanguageChange,
    puzzleType,
    onPuzzleTypeChange,
    enabled,
    onEnabledChange,
    tagBasedCrossword,
    onTagBasedCrosswordChange,
    memo,
    onMemoChange,
    onSubmit,
    onCancel,
    isEdit
}) {
    const puzzleTypes = getAllPuzzleTypes();

    return (
        <form onSubmit={onSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 600 }}>
                <FormControlLabel
                    control={<Switch checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)} />}
                    label="Enabled"
                />

                <CronExpressionBuilder
                    value={cron}
                    onChange={onCronChange}
                    presets={ADMIN_CRON_PRESETS}
                />

                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Language
                    </Typography>
                    <ToggleButtonGroup
                        value={language}
                        exclusive
                        onChange={(e, newLang) => newLang != null && onLanguageChange(newLang)}
                        fullWidth
                        size="small"
                    >
                        {LANGUAGES.map((lang) => (
                            <ToggleButton key={lang.value} value={lang.value}>
                                {lang.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Puzzle Type
                    </Typography>
                    <ToggleButtonGroup
                        value={puzzleType}
                        exclusive
                        onChange={(e, newType) => newType != null && onPuzzleTypeChange(newType)}
                        size="small"
                        sx={{ flexWrap: "wrap", gap: 0.5 }}
                    >
                        {puzzleTypes.map((pt) => (
                            <ToggleButton key={pt.id} value={pt.id}>
                                {pt.name}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {puzzleType === "CROSSWORD" && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={tagBasedCrossword}
                                    onChange={(e) => onTagBasedCrosswordChange(e.target.checked)}
                                />
                            }
                            label="Tag-based crossword selection"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 4, mt: -0.5, maxWidth: 520 }}>
                            When enabled, published crosswords are chosen using server-configured tags (not editable here). Only applies to crossword schedules.
                            Tags are listed under{" "}
                            <Typography component="span" variant="caption" sx={{ fontFamily: "ui-monospace, monospace" }}>
                                app.publisher.dailyCrosswordTags
                            </Typography>{" "}
                            in{" "}
                            <Typography component="span" variant="caption" sx={{ fontFamily: "ui-monospace, monospace" }}>
                                application.yml
                            </Typography>
                            , for example:
                        </Typography>
                        <Box
                            component="pre"
                            sx={{
                                pl: 4,
                                mt: 1,
                                ml: 0,
                                mr: 0,
                                p: 1.5,
                                borderRadius: 1,
                                border: 1,
                                borderColor: "divider",
                                bgcolor: "action.hover",
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                fontSize: "0.75rem",
                                lineHeight: 1.5,
                                overflow: "auto",
                                maxWidth: 520,
                                whiteSpace: "pre",
                            }}
                        >
                            {`app:
  publisher:
    dailyCrosswordTags:
      - common`}
                        </Box>
                    </Box>
                )}

                <TextField
                    label="Memo"
                    value={memo}
                    onChange={(e) => onMemoChange(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    placeholder="Optional notes for this schedule"
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button type="button" variant="outlined" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {isEdit ? "Save" : "Create"}
                    </Button>
                </Box>
            </Box>
        </form>
    );
}
